import { useState, useCallback } from 'react';
import * as api from '../api/client';

export function useMessages(activeId) {
  const [messages, setMessages] = useState([]);

  const load = useCallback(async (sid) => {
    const history = await api.getSession(sid);
    setMessages(
      history
        .filter(m => m.role !== 'system')
        .map(m => ({ ...m, persistent: true, source: 'history' }))
    );
  }, []);

  const clear = useCallback(() => setMessages([]), []);

  const addMessage = useCallback((msg) => {
    const newMsg = { ...msg, persistent: false, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, newMsg]);
  }, []);

  /**
   * Append a text delta to the last message in the list.
   * Used during streaming to grow the assistant bubble token by token.
   * If the last message isn't an assistant message, this is a no-op.
   */
  const appendToLastMessage = useCallback((delta) => {
    setMessages(prev => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      if (last.role !== 'assistant') return prev;
      const updated = { ...last, content: (last.content || '') + delta };
      return [...prev.slice(0, -1), updated];
    });
  }, []);

  /**
   * Replace the last message entirely.
   * Used to swap a streaming placeholder (e.g. a tool-status message)
   * with the real content once streaming begins.
   */
  const updateLastMessage = useCallback((patch) => {
    setMessages(prev => {
      if (!prev.length) return prev;
      const updated = { ...prev[prev.length - 1], ...patch };
      return [...prev.slice(0, -1), updated];
    });
  }, []);

  const markLastExchangePersistent = useCallback(() => {
    setMessages(prev => {
      const updated = [...prev];
      let assistantMarked = false;
      let userMarked = false;
      for (let i = updated.length - 1; i >= 0; i--) {
        const role = updated[i].role;
        if (!assistantMarked && role === 'assistant') {
          updated[i] = { ...updated[i], persistent: true };
          assistantMarked = true;
        } else if (!userMarked && role === 'user') {
          updated[i] = { ...updated[i], persistent: true };
          userMarked = true;
        }
        if (assistantMarked && userMarked) break;
      }
      return updated;
    });
  }, []);

  const togglePersistent = useCallback(async (index) => {
    const msg = messages[index];
    if (!msg || msg.role === 'error') return;

    const wasPersistent = msg.persistent;

    if (!wasPersistent) {
      setMessages(prev =>
        prev.map((m, i) => (i === index ? { ...m, persistent: true } : m))
      );
      if (activeId) {
        try {
          await api.pinMessage(activeId, msg.role, msg.content);
        } catch {
          setMessages(prev =>
            prev.map((m, i) => (i === index ? { ...m, persistent: false } : m))
          );
        }
      }
    } else {
      setMessages(prev => prev.filter((_, i) => i !== index));
      if (activeId) {
        try {
          await api.unpinMessage(activeId, msg.role, msg.content);
        } catch {
          setMessages(prev => [
            ...prev.slice(0, index),
            { ...msg },
            ...prev.slice(index),
          ]);
        }
      }
    }
  }, [messages, activeId]);

  return {
    messages,
    setMessages,
    load,
    clear,
    addMessage,
    appendToLastMessage,
    updateLastMessage,
    markLastExchangePersistent,
    togglePersistent,
  };
}
